import { BookCard } from '@/components/learning/book-card'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { bookCategoryColors, bookCategoryLabels } from '@/constants/learning'
import { useBooks } from '@/hooks/use-learning'
import type { BookCategory } from '@/types'
import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

const ALL_CATEGORIES: BookCategory[] = [
  'career_development',
  'leadership',
  'communication',
  'productivity',
  'technical',
  'entrepreneurship',
  'personal_growth',
  'job_search',
  'networking',
  'finance',
]

export function LearnBooksPage() {
  const [selectedCategory, setSelectedCategory] = useState<BookCategory | undefined>()
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useBooks(selectedCategory)
  const navigate = useNavigate()

  const books = data?.pages.flatMap((page) => page.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  // Infinite scroll: observe a sentinel element at the bottom
  const sentinelRef = useRef<HTMLDivElement>(null)

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(handleObserver, {
      rootMargin: '200px',
    })
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [handleObserver])

  return (
    <div className="h-full overflow-y-auto">
      <div className="w-full px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link to="/learn">
            <Button variant="ghost" size="sm" className="mb-2 -ml-2 gap-1">
              <ArrowLeft className="h-4 w-4" />
              Back to Learn
            </Button>
          </Link>
          <h1 className="text-3xl font-bold mb-1">Recommended Reading</h1>
          <p className="text-muted-foreground">
            Curated career books with AI-generated summaries and insights
          </p>
        </div>

        {/* Category Filter */}
        <div className="mb-6 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSelectedCategory(undefined)}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
              !selectedCategory
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted hover:bg-muted/80 text-muted-foreground'
            }`}
          >
            All
          </button>
          {ALL_CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                selectedCategory === cat
                  ? `${bookCategoryColors[cat]}`
                  : 'bg-muted hover:bg-muted/80 text-muted-foreground'
              }`}
            >
              {bookCategoryLabels[cat]}
            </button>
          ))}
        </div>

        {/* Book Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
              <p className="text-muted-foreground">Failed to load books. Please try again later.</p>
            </CardContent>
          </Card>
        ) : books.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No books found in this category.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {total > 0 && (
              <p className="text-sm text-muted-foreground mb-4">
                {books.length} of {total} books
              </p>
            )}

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {books.map((book) => (
                <BookCard
                  key={book.id}
                  book={book}
                  onClick={() => navigate(`/learn/books/${book.id}`)}
                />
              ))}
            </div>

            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} className="h-1" />

            {isFetchingNextPage && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
